import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Send, Building2, MapPin, ChevronRight, Clock } from 'lucide-react';

interface Client { id?: string; _id?: string; name?: string; trading_name?: string; broker_id?: string | null }
interface Site { id?: string; _id?: string; name?: string; site_reference?: string; default_survey_template_id?: string | null }
interface Template { id?: string; _id?: string; name?: string; current_published_version?: number | null }
interface Batch { id: string; client_name: string; template_name: string; status: string; delivery_path: string; due_date?: string; sites: BatchSite[]; created_at: string }
interface BatchSite { site_id: string; site_reference?: string; site_name?: string; included?: boolean; allocation?: { mode?: string; recipient_name?: string; recipient_email?: string; recipient_phone?: string; instruction_id?: string; status?: string } | null }

const STATUS_LABEL: Record<string, { label: string; variant: any }> = {
  pending_broker_review: { label: 'Awaiting broker', variant: 'cyan' },
  pending_client_allocation: { label: 'Awaiting client', variant: 'blue' },
  completed: { label: 'Allocated', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'muted' },
};

export const IssueSurveyPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [clientId, setClientId] = useState('');
  const [path, setPath] = useState<'broker' | 'client'>('broker');
  const [dueDate, setDueDate] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [siteTpl, setSiteTpl] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string>('');
  const [cfg, setCfg] = useState<{ nudge_every_days: number; escalate_after_days: number; enabled: boolean }>({ nudge_every_days: 3, escalate_after_days: 7, enabled: true });
  const [savingCfg, setSavingCfg] = useState(false);

  const loadBatches = () => api.get<Batch[]>('/survey-batches').then(r => setBatches(r.data)).catch(() => {});

  useEffect(() => {
    api.get<Client[]>('/clients').then(r => setClients(r.data)).catch(() => {});
    api.get<Template[]>('/templates').then(r => setTemplates(r.data.filter(t => t.current_published_version))).catch(() => {});
    api.get('/survey-batches/config/reminders').then(r => setCfg(r.data as any)).catch(() => {});
    loadBatches();
  }, []);

  useEffect(() => {
    setSelected(new Set());
    setSiteTpl({});
    if (!clientId) { setSites([]); return; }
    api.get<Site[]>('/sites', { params: { client_id: clientId } }).then(r => setSites(r.data)).catch(() => setSites([]));
  }, [clientId]);

  const client = useMemo(() => clients.find(c => (c.id || c._id) === clientId), [clients, clientId]);
  const clientHasBroker = !!client?.broker_id;

  useEffect(() => { if (!clientHasBroker) setPath('client'); }, [clientHasBroker]);

  const toggle = (sid: string) => {
    setSelected(prev => {
      const n = new Set(prev); n.has(sid) ? n.delete(sid) : n.add(sid); return n;
    });
    setSiteTpl(prev => {
      if (prev[sid] !== undefined) return prev;
      const s = sites.find(x => (x.id || x._id) === sid);
      return { ...prev, [sid]: s?.default_survey_template_id || '' };
    });
  };

  const setTplFor = (sid: string, tid: string) => setSiteTpl(prev => ({ ...prev, [sid]: tid }));
  const tplName = (tid: string) => templates.find(t => (t.id || t._id) === tid)?.name || '';
  const selectedSites = useMemo(() => sites.filter(s => selected.has((s.id || s._id) as string)), [sites, selected]);
  const allHaveTpl = selectedSites.length > 0 && selectedSites.every(s => siteTpl[(s.id || s._id) as string]);

  const doIssue = () => api.post('/survey-batches', {
    client_id: clientId,
    sites: selectedSites.map(s => ({ site_id: (s.id || s._id) as string, template_id: siteTpl[(s.id || s._id) as string] })),
    delivery_path: path,
    due_date: dueDate || null,
  });

  const onIssued = () => {
    toast.success(path === 'broker' ? 'Issued to broker for review.' : 'Issued to client for allocation.');
    setClientId(''); setSelected(new Set()); setSiteTpl({}); setDueDate('');
    loadBatches();
  };

  const geocodeAndRetry = async () => {
    setBusy(true);
    try {
      toast.info('Geocoding site addresses…');
      await api.post('/sites/regeocode-batch');
      await doIssue();
      onIssued();
    } catch (e2: any) {
      const d = e2?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.detail) || 'Some sites still have no coordinates — check their addresses in Sites.');
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!clientId || selected.size === 0) { toast.error('Pick a client and at least one site.'); return; }
    if (!allHaveTpl) { toast.error('Allocate a survey template to every selected site.'); return; }
    setBusy(true);
    try {
      await doIssue();
      onIssued();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'sites_need_geocoding') {
        toast.error(d.detail, { duration: 10000, action: { label: 'Geocode & retry', onClick: () => void geocodeAndRetry() } });
      } else {
        toast.error((typeof d === 'string' ? d : d?.detail) || 'Could not issue survey.');
      }
    } finally { setBusy(false); }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put('/survey-batches/config/reminders', cfg);
      toast.success('Reminder settings saved.');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not save settings.');
    } finally { setSavingCfg(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Issue Survey</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Send a survey batch to a broker for approval, or straight to the client to allocate.</p>
      </div>

      <Card data-testid="issue-wizard">
        <CardHeader><CardTitle className="flex items-center gap-2"><Send size={18} /> New survey batch</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Client *</Label>
              <Select data-testid="issue-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.trading_name || c.name}</option>)}
              </Select>
            </div>
          </div>

          {clientId && (
            <div className="space-y-1.5">
              <Label>Sites * <span className="text-raptor-navy/50 font-body">({selected.size} selected)</span></Label>
              <div className="border border-border/70 rounded-md max-h-56 overflow-auto divide-y divide-border/50" data-testid="issue-sites">
                {sites.length === 0 && <div className="p-3 text-sm text-raptor-navy/50 font-body">No sites for this client.</div>}
                {sites.map(s => {
                  const sid = (s.id || s._id) as string;
                  return (
                    <label key={sid} className="flex items-center gap-3 px-3 py-2 hover:bg-raptor-neutral/40 cursor-pointer">
                      <input type="checkbox" checked={selected.has(sid)} onChange={() => toggle(sid)} data-testid={`issue-site-${sid}`} />
                      <MapPin size={14} className="text-raptor-blue" />
                      <span className="text-sm text-raptor-navy">{s.name || s.site_reference}</span>
                      {s.site_reference && <span className="text-[11px] text-raptor-navy/50 font-tabular">{s.site_reference}</span>}
                      {s.default_survey_template_id
                        ? <span className="ml-auto text-[11px] text-raptor-navy/50 font-body">default: {tplName(s.default_survey_template_id) || '—'}</span>
                        : <span className="ml-auto text-[11px] text-amber-600 font-body">no default template</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {selectedSites.length > 0 && (
            <div className="space-y-1.5" data-testid="issue-review">
              <Label>Review &amp; allocate templates <span className="text-raptor-navy/50 font-body">— confirm the template for each site before sending</span></Label>
              <div className="border border-border/70 rounded-md overflow-hidden">
                <table className="w-full text-sm font-body">
                  <thead className="bg-raptor-neutral/40 text-[11px] uppercase text-raptor-navy/60">
                    <tr>
                      <th className="py-2 px-3 text-left">Site</th>
                      <th className="py-2 px-3 text-left w-[46%]">Survey template *</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {selectedSites.map(s => {
                      const sid = (s.id || s._id) as string;
                      const val = siteTpl[sid] || '';
                      return (
                        <tr key={sid} data-testid={`issue-review-${sid}`} className={val ? '' : 'bg-amber-50/40'}>
                          <td className="py-2 px-3">
                            <div className="text-raptor-navy">{s.name || s.site_reference}</div>
                            {s.site_reference && <div className="text-[11px] text-raptor-navy/50 font-tabular">{s.site_reference}</div>}
                          </td>
                          <td className="py-2 px-3">
                            <Select value={val} onChange={(e) => setTplFor(sid, e.target.value)} data-testid={`issue-review-tpl-${sid}`}>
                              <option value="">— Select template —</option>
                              {templates.map(t => <option key={t.id || t._id} value={t.id || t._id}>{t.name}</option>)}
                            </Select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!allHaveTpl && <div className="text-[11px] text-amber-600 font-body">Every selected site needs a template before you can send.</div>}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Delivery path *</Label>
              <div className="flex gap-2">
                <Button type="button" variant={path === 'broker' ? 'gradient' : 'outline'} size="sm" disabled={!clientHasBroker}
                  onClick={() => setPath('broker')} data-testid="issue-path-broker">To broker (approval)</Button>
                <Button type="button" variant={path === 'client' ? 'gradient' : 'outline'} size="sm"
                  onClick={() => setPath('client')} data-testid="issue-path-client">Straight to client</Button>
              </div>
              {!clientHasBroker && <div className="text-[11px] text-raptor-navy/50 font-body">This client has no broker — issuing directly to the client.</div>}
            </div>
            <div className="space-y-1.5">
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="issue-due" />
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="gradient" disabled={busy || !allHaveTpl} onClick={submit} data-testid="issue-submit">
              <Send size={14} /> {busy ? 'Issuing…' : 'Issue survey'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="reminder-config">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Clock size={18} /> Overdue reminder settings</CardTitle>
          <CardDescription>Control how often clients who haven&apos;t allocated a survey are nudged, and when to escalate to their broker.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label>Nudge client every (days)</Label>
            <Input type="number" min={1} max={60} className="w-40" value={cfg.nudge_every_days}
              onChange={(e) => setCfg({ ...cfg, nudge_every_days: Number(e.target.value) })} data-testid="cfg-nudge-days" />
          </div>
          <div className="space-y-1.5">
            <Label>Escalate to broker after (days overdue)</Label>
            <Input type="number" min={1} max={180} className="w-56" value={cfg.escalate_after_days}
              onChange={(e) => setCfg({ ...cfg, escalate_after_days: Number(e.target.value) })} data-testid="cfg-escalate-days" />
          </div>
          <label className="flex items-center gap-2 text-sm text-raptor-navy font-body pb-2">
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="cfg-enabled" />
            Reminders enabled
          </label>
          <Button variant="gradient" size="sm" className="ml-auto" disabled={savingCfg} onClick={saveCfg} data-testid="cfg-save">
            {savingCfg ? 'Saving…' : 'Save settings'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Issued batches</CardTitle><CardDescription>Track approval and allocation progress.</CardDescription></CardHeader>
        <CardContent>
          <div className="divide-y divide-border/50" data-testid="issue-batches">
            {batches.length === 0 && <div className="text-sm text-raptor-navy/50 font-body py-3">No batches issued yet.</div>}
            {batches.map(b => {
              const st = STATUS_LABEL[b.status] || { label: b.status, variant: 'muted' };
              const included = (b.sites || []).filter((s) => s.included).length;
              const allocated = (b.sites || []).filter((s) => s.included && s.allocation).length;
              const pct = included > 0 ? Math.round((allocated / included) * 100) : 0;
              const isOpen = expanded === b.id;
              return (
                <div key={b.id} data-testid={`batch-row-${b.id}`}>
                  <button
                    className="w-full py-3 flex items-center justify-between text-left hover:bg-raptor-neutral/30 rounded-md px-2 -mx-2"
                    onClick={() => setExpanded(isOpen ? '' : b.id)}
                    data-testid={`batch-expand-${b.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <ChevronRight size={16} className={`text-raptor-navy/40 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      <Building2 size={16} className="text-raptor-blue shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-raptor-navy truncate">{b.client_name} <span className="text-raptor-navy/50 font-normal">· {b.template_name}</span></div>
                        <div className="text-[11px] text-raptor-navy/50 font-body">{b.delivery_path === 'broker' ? 'via broker' : 'direct to client'}{b.due_date ? ` · due ${b.due_date}` : ''}</div>
                        <div className="flex items-center gap-2 mt-1.5 w-56 max-w-full" data-testid={`batch-progress-${b.id}`}>
                          <div className="flex-1 h-1.5 rounded-full bg-raptor-neutral overflow-hidden">
                            <div className="h-full rounded-full bg-raptor-blue transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] text-raptor-navy/60 font-tabular whitespace-nowrap">{allocated} of {included} allocated</span>
                        </div>
                      </div>
                    </div>
                    <Badge variant={st.variant}>{st.label}</Badge>
                  </button>
                  {isOpen && (
                    <div className="ml-9 mb-3 border border-border/60 rounded-md divide-y divide-border/50 bg-white" data-testid={`batch-detail-${b.id}`}>
                      {(b.sites || []).map((s) => {
                        const a = s.allocation;
                        return (
                          <div key={s.site_id} className="px-3 py-2 flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2 min-w-0">
                              <MapPin size={13} className={`mt-0.5 ${s.included ? 'text-raptor-blue' : 'text-raptor-navy/30'}`} />
                              <div className="min-w-0">
                                <div className={`text-sm ${s.included ? 'text-raptor-navy' : 'text-raptor-navy/40 line-through'}`}>{s.site_name || s.site_reference}</div>
                                {a ? (
                                  <div className="text-[11px] text-raptor-navy/60 font-body">
                                    {a.mode === 'self' ? 'Self-assessment' : 'Site contact'} → {a.recipient_name || '—'}
                                    {a.recipient_email ? ` · ${a.recipient_email}` : ''}{a.recipient_phone ? ` · ${a.recipient_phone}` : ''}
                                  </div>
                                ) : (
                                  <div className="text-[11px] text-raptor-navy/40 font-body">{s.included ? 'Not yet allocated' : 'Excluded by broker'}</div>
                                )}
                              </div>
                            </div>
                            {a ? <Badge variant="success">Sent</Badge> : s.included ? <Badge variant="muted">Pending</Badge> : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
